import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import api from './api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import RuhsatListesi from './pages/RuhsatListesi';
import RuhsatDetay from './pages/RuhsatDetay';
import RuhsatForm from './pages/RuhsatForm';
import SicilListesi from './pages/SicilListesi';
import SicilForm from './pages/SicilForm';
import Tanimlar from './pages/Tanimlar';
import Kullanicilar from './pages/Kullanicilar';
import Ayarlar from './pages/Ayarlar';
import IslemLog from './pages/IslemLog';
import SikayetListesi from './pages/SikayetListesi';
import DenetimListesi from './pages/DenetimListesi';
import HarcTakip from './pages/HarcTakip';
import YaptirimListesi from './pages/YaptirimListesi';
import VadesiYaklasan from './pages/VadesiYaklasan';
import Raporlar from './pages/Raporlar';
import Harita from './pages/Harita';
import Gorevler from './pages/Gorevler';
import YaziSablonlari from './pages/YaziSablonlari';
import DenetimTakvimi from './pages/DenetimTakvimi';
import SmsSablonlari from './pages/SmsSablonlari';

export const AuthContext = createContext(null);
function useAuth() { return useContext(AuthContext); }

function PrivateRoute({ children }) {
  const { kullanici } = useAuth();
  return kullanici ? children : <Navigate to="/giris" replace />;
}

const NAV_GROUPS = [
  { label:'Ruhsat İşlemleri', icon:'📋', items:[
    {to:'/ruhsat',icon:'📋',label:'Ruhsat Listesi'}, {to:'/ruhsat/yeni',icon:'＋',label:'Yeni Başvuru'}, {to:'/sicil',icon:'👤',label:'Sicil Kayıtları'}]},
  { label:'Denetim ve Takip', icon:'🛡️', items:[
    {to:'/sikayet',icon:'⚠️',label:'Şikayet / İhbar'}, {to:'/denetim',icon:'🔍',label:'Denetim Planı'}, {to:'/yaptirim',icon:'🚫',label:'Yaptırımlar'}, {to:'/harc',icon:'💰',label:'Harç Takibi'}]},
  { label:'Raporlar & Araçlar', icon:'📈', items:[
    {to:'/vadesi-yaklasan',icon:'⏰',label:'Vadesi Yaklaşan'}, {to:'/raporlar',icon:'📈',label:'Raporlar'}, {to:'/gorevler',icon:'✓',label:'Görevler'},
    {divider:true}, {to:'/harita',icon:'🗺️',label:'Harita'}, {to:'/denetim-takvimi',icon:'📅',label:'Denetim Takvimi'}, {to:'/yazi-sablonlari',icon:'📄',label:'Yazı Şablonları'}]},
  { label:'Yönetim', icon:'⚙️', items:[
    {to:'/tanimlar',icon:'📚',label:'Tanımlar'}, {to:'/kullanicilar',icon:'👥',label:'Kullanıcılar',rol:'admin'},
    {to:'/islem-log',icon:'📜',label:'İşlem Logları',roller:['admin','mudur']},
    {divider:true},
    {to:'/sms-sablonlari',icon:'📱',label:'SMS Şablonları',roller:['admin','mudur','superadmin']},
    {to:'/ayarlar',icon:'⚙️',label:'Sistem Ayarları',roller:['superadmin']}]},
];

const izinli = (item, kullanici) => {
  if (kullanici?.rol === 'superadmin') return true;
  return (!item.rol || kullanici?.rol === item.rol) && (!item.roller || item.roller.includes(kullanici?.rol));
};

function TopNavigation({ mobileOpen, setMobileOpen }) {
  const { kullanici } = useAuth();
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname, setMobileOpen]);
  return <nav className={`main-navbar ${mobileOpen?'mobile-open':''}`} aria-label="Ana menü">
    <div className="nav-shell">
      <div className="horizontal-menu">
        <NavLink to="/dashboard" className={({isActive})=>`top-nav-link ${isActive?'active':''}`}><span>📊</span> Dashboard</NavLink>
        {NAV_GROUPS.map(group=>{
          const items=group.items.filter(x=>x.divider||izinli(x,kullanici));
          const active=items.some(x=>x.to&&location.pathname.startsWith(x.to));
          return <div className={`top-nav-group ${active?'active':''}`} key={group.label}>
            <button className="top-nav-link dropdown-trigger" type="button"><span>{group.icon}</span>{group.label}<span className="nav-arrow">▾</span></button>
            <div className="top-nav-dropdown">
              <div className="dropdown-heading">{group.label}</div>
              {items.map((item,i)=>item.divider?<div className="dropdown-divider" key={`d-${i}`}/>:<NavLink key={item.to} to={item.to} className={({isActive})=>isActive?'active':''}><span className="dropdown-icon">{item.icon}</span><span>{item.label}</span></NavLink>)}
            </div>
          </div>;
        })}
      </div>
      <NavLink to="/ruhsat/yeni" className="quick-license-action">＋ Yeni Ruhsat Başvurusu</NavLink>
    </div>
  </nav>;
}

function Layout({ children }) {
  const { kullanici, logout } = useAuth();
  const [mobileOpen,setMobileOpen]=useState(false);
  const initials=(kullanici?.ad_soyad||'K').split(/\s+/).slice(0,2).map(x=>x[0]).join('').toLocaleUpperCase('tr-TR');
  return <div className="app-layout top-layout">
    <header className="app-header">
      <div className="brand-block"><div className="brand-mark"><img src="/ub.png" alt="Ünye Belediyesi logosu" /></div><div><strong>ÜNYE BELEDİYESİ</strong><span>İşyeri Ruhsat Yönetim Sistemi</span></div></div>
      <div className="header-actions">
        <BildirimZili />
        <div className="user-profile"><div className="user-avatar">{initials}</div><div className="user-copy"><strong>{kullanici?.ad_soyad}</strong><span>{kullanici?.rol?.toLocaleUpperCase('tr-TR')}</span></div></div>
        <button className="header-logout" onClick={logout} title="Çıkış yap">Çıkış</button>
        <button className="mobile-menu-button" onClick={()=>setMobileOpen(v=>!v)} aria-expanded={mobileOpen} aria-label="Menüyü aç veya kapat">☰</button>
      </div>
    </header>
    <TopNavigation mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}/>
    <main className="main-content"><div className="page-content">{children}</div></main>
  </div>;
}

function BildirimZili() {
  const [okunmamis, setOkunmamis] = useState(0);
  const [acik, setAcik] = useState(false);
  const [bildirimler, setBildirimler] = useState([]);
  const panelRef = useRef(null);

  useEffect(() => {
    const yukle = () => api.get('/bildirim').then(r => { setOkunmamis(r.data.okunmamis||0); setBildirimler(r.data.veriler||[]); }).catch(()=>{});
    yukle();
    const t = setInterval(yukle, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setAcik(false); };
    if (acik) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [acik]);

  const oku = async (id) => {
    await api.patch(`/bildirim/${id}/oku`).catch(()=>{});
    setBildirimler(b => b.map(x => x.id===id ? {...x,okundu:true} : x));
    setOkunmamis(c => Math.max(0,c-1));
  };

  const tumuOku = async () => {
    await api.patch('/bildirim/tumu-oku').catch(()=>{});
    setBildirimler(b => b.map(x=>({...x,okundu:true})));
    setOkunmamis(0);
  };

  const fmt = d => d ? new Date(d).toLocaleString('tr-TR',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '';

  return (
    <div style={{position:'relative'}} ref={panelRef}>
      <button className="header-icon-button" onClick={()=>setAcik(a=>!a)} aria-label="Bildirimler">
        🔔
        {okunmamis > 0 && (
          <span style={{position:'absolute',top:'2px',right:'2px',background:'#e53935',color:'#fff',borderRadius:'50%',fontSize:'9px',fontWeight:'700',width:'16px',height:'16px',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>
            {okunmamis > 9 ? '9+' : okunmamis}
          </span>
        )}
      </button>
      {acik && (
        <div style={{position:'absolute',right:0,top:'calc(100% + 8px)',width:'320px',background:'#fff',border:'1px solid #ddd',borderRadius:'8px',boxShadow:'0 8px 24px rgba(0,0,0,0.15)',zIndex:1000,maxHeight:'400px',overflowY:'auto'}}>
          <div style={{padding:'10px 14px',borderBottom:'1px solid #eee',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <strong style={{fontSize:'14px',color:'#1a237e'}}>Bildirimler</strong>
            {okunmamis > 0 && <button onClick={tumuOku} style={{fontSize:'11px',color:'#3949ab',background:'none',border:'none',cursor:'pointer'}}>Tümünü Oku</button>}
          </div>
          {bildirimler.length === 0 ? (
            <div style={{padding:'20px',textAlign:'center',color:'#aaa',fontSize:'13px'}}>Bildirim yok</div>
          ) : bildirimler.slice(0,20).map(b=>(
            <div key={b.id} onClick={()=>oku(b.id)} style={{padding:'10px 14px',borderBottom:'1px solid #f5f5f5',cursor:'pointer',background:b.okundu?'#fff':'#f5f7ff',transition:'background 0.1s'}}
              onMouseOver={e=>e.currentTarget.style.background='#eef0ff'} onMouseOut={e=>e.currentTarget.style.background=b.okundu?'#fff':'#f5f7ff'}>
              <div style={{fontSize:'13px',fontWeight:b.okundu?'400':'600',color:'#333',marginBottom:'2px'}}>{b.baslik}</div>
              {b.icerik && <div style={{fontSize:'11px',color:'#666',marginBottom:'3px'}}>{b.icerik}</div>}
              <div style={{fontSize:'10px',color:'#aaa'}}>{fmt(b.olusturma_tarihi)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [kullanici, setKullanici] = useState(() => {
    try { return JSON.parse(localStorage.getItem('kullanici')); } catch { return null; }
  });

  const login = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('kullanici', JSON.stringify(data.kullanici));
    setKullanici(data.kullanici);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('kullanici');
    setKullanici(null);
  };

  return (
    <AuthContext.Provider value={{ kullanici, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/giris" element={<Login />} />
          <Route path="/" element={<PrivateRoute><Layout><Navigate to="/dashboard" replace /></Layout></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
          <Route path="/ruhsat" element={<PrivateRoute><Layout><RuhsatListesi /></Layout></PrivateRoute>} />
          <Route path="/ruhsat/yeni" element={<PrivateRoute><Layout><RuhsatForm /></Layout></PrivateRoute>} />
          <Route path="/ruhsat/:id" element={<PrivateRoute><Layout><RuhsatDetay /></Layout></PrivateRoute>} />
          <Route path="/ruhsat/:id/duzenle" element={<PrivateRoute><Layout><RuhsatForm /></Layout></PrivateRoute>} />
          <Route path="/sicil" element={<PrivateRoute><Layout><SicilListesi /></Layout></PrivateRoute>} />
          <Route path="/sicil/yeni" element={<PrivateRoute><Layout><SicilForm /></Layout></PrivateRoute>} />
          <Route path="/sicil/:id/duzenle" element={<PrivateRoute><Layout><SicilForm /></Layout></PrivateRoute>} />
          <Route path="/tanimlar" element={<PrivateRoute><Layout><Tanimlar /></Layout></PrivateRoute>} />
          <Route path="/kullanicilar" element={<PrivateRoute><Layout><Kullanicilar /></Layout></PrivateRoute>} />
          <Route path="/ayarlar" element={<PrivateRoute><Layout><Ayarlar /></Layout></PrivateRoute>} />
          <Route path="/islem-log" element={<PrivateRoute><Layout><IslemLog /></Layout></PrivateRoute>} />
          <Route path="/sikayet" element={<PrivateRoute><Layout><SikayetListesi /></Layout></PrivateRoute>} />
          <Route path="/denetim" element={<PrivateRoute><Layout><DenetimListesi /></Layout></PrivateRoute>} />
          <Route path="/yaptirim" element={<PrivateRoute><Layout><YaptirimListesi /></Layout></PrivateRoute>} />
          <Route path="/harc" element={<PrivateRoute><Layout><HarcTakip /></Layout></PrivateRoute>} />
          <Route path="/vadesi-yaklasan" element={<PrivateRoute><Layout><VadesiYaklasan /></Layout></PrivateRoute>} />
          <Route path="/raporlar" element={<PrivateRoute><Layout><Raporlar /></Layout></PrivateRoute>} />
          <Route path="/harita" element={<PrivateRoute><Layout><Harita /></Layout></PrivateRoute>} />
          <Route path="/gorevler" element={<PrivateRoute><Layout><Gorevler /></Layout></PrivateRoute>} />
          <Route path="/denetim-takvimi" element={<PrivateRoute><Layout><DenetimTakvimi /></Layout></PrivateRoute>} />
          <Route path="/yazi-sablonlari" element={<PrivateRoute><Layout><YaziSablonlari /></Layout></PrivateRoute>} />
          <Route path="/sms-sablonlari" element={<PrivateRoute><Layout><SmsSablonlari /></Layout></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
